import { useEffect, useState, useCallback } from 'react';
import type { CollectionListSnapshot } from '@/types/collectionList';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, MapPinIcon, ShareIcon } from '@heroicons/react/24/outline';
import { decodeHtmlEntities } from '@member-portal/utils';
import { themeClasses, Button } from '@member-portal/ui';
import { Hotel, HotelImage } from '@/components/HotelCard';
import ImageSlideshow from '@/components/ImageSlideshow';
import MapProvider from '@/components/MapProvider';
import LocationMap from '@/components/LocationMap';
import { SubmitRequestButton } from '@/components/SubmitRequestButton';
import { useAuth } from '@/contexts/AuthContext';

// Strip inline styles from HTML to use theme styling
function stripInlineStyles(html: string): string {
  return decodeHtmlEntities(html)
    .replace(/\s*style="[^"]*"/gi, '')
    .replace(/\s*style='[^']*'/gi, '')
    .replace(/<font[^>]*>/gi, '')
    .replace(/<\/font>/gi, '')
    .replace(/<span[^>]*>(.*?)<\/span>/gi, '$1');
}

function HotelDetail() {
  const { hotelId } = useParams<{ hotelId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if this is a share view (standalone, no navigation)
  const isShareView = searchParams.get('share') === 'true';
  const { isAuthenticated } = useAuth();

  const collectionList = (location.state as { collectionList?: CollectionListSnapshot } | null)
    ?.collectionList;

  const navigateBackToCollection = useCallback(() => {
    navigate('/collection', collectionList ? { state: { restore: collectionList } } : {});
  }, [navigate, collectionList]);

  const [hotel, setHotel] = useState<Hotel | null>(location.state?.hotel || null);
  const [loading, setLoading] = useState(!location.state?.hotel);
  const [error, setError] = useState<string | null>(null);
  const [showSignUpPopup, setShowSignUpPopup] = useState(false);

  // Signed-out: show unlock modal as soon as property detail has loaded (not on share links).
  useEffect(() => {
    if (isShareView || isAuthenticated || loading || error || !hotel) return;
    setShowSignUpPopup(true);
  }, [isShareView, isAuthenticated, loading, error, hotel]);

  // Always fetch from API to get images (navigation state won't have them)
  useEffect(() => {
    const loadHotel = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/vendors/${hotelId}`);
        if (!response.ok) {
          throw new Error('Hotel not found');
        }
        const result = await response.json();
        const data = result.data || result;
        setHotel(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load hotel');
      } finally {
        setLoading(false);
      }
    };

    if (hotelId) {
      loadHotel();
    }
  }, [hotelId]);

  // Build images array - use images from API or fall back to single imageUrl
  const getImages = (): HotelImage[] => {
    if (!hotel) return [];

    if (hotel.images && hotel.images.length > 0) {
      return hotel.images;
    }

    // Fallback to single image if no images array
    if (hotel.imageUrl) {
      return [{ id: 'main', url: hotel.imageUrl }];
    }

    return [];
  };

  const formatLocation = () => {
    if (!hotel) return '';
    const parts = [hotel.city, hotel.state, hotel.country].filter(Boolean);
    return parts.join(', ');
  };

  // Parse benefits string (pipe-separated) into array
  const parseBenefits = (benefits?: string) => {
    if (!benefits) return [];
    return benefits
      .split('|')
      .map((b) => decodeHtmlEntities(b.trim()))
      .filter(Boolean);
  };

  if (loading) {
    return (
      <div className={`${isShareView ? 'pt-8' : 'fhxd-chrome-top'} min-h-screen bg-theme-bg`}>
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-text"></div>
        </div>
      </div>
    );
  }

  if (error || !hotel) {
    return (
      <div className={`${isShareView ? 'pt-8' : 'fhxd-chrome-top'} min-h-screen bg-theme-bg`}>
        <div className={`${themeClasses.container} py-16 text-center`}>
          <p className="text-red-600 mb-4">{error || 'Hotel not found'}</p>
          <Button variant="secondary" onClick={navigateBackToCollection}>
            Back to Collection
          </Button>
        </div>
      </div>
    );
  }

  const benefits = parseBenefits(hotel.benefits);
  const description = hotel.websiteDescription || hotel.description || '';

  const images = getImages();

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/collection/${hotelId}?share=true`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: hotel.name,
          text: `Check out ${hotel.name} on FHxD`,
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled or share failed, fall back to clipboard
        copyToClipboard(shareUrl);
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Could add a toast notification here
    alert('Link copied to clipboard!');
  };

  return (
    <div className={`${isShareView ? 'pt-8' : 'fhxd-chrome-top'} min-h-screen bg-theme-bg`}>
      <div className={themeClasses.container}>
        {/* Back button - hidden in share view */}
        {!isShareView && (
          <div className="py-6">
            <button
              type="button"
              onClick={navigateBackToCollection}
              className={`inline-flex items-center ${themeClasses.text.button} text-theme-text-muted hover:text-theme-text transition-colors`}
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Collection
            </button>
          </div>
        )}

        {/* Title & Location */}
        <div className="space-y-4 mb-8">
          <div className="flex items-start justify-between gap-4">
            <h1 className={themeClasses.title.h1}>{hotel.name}</h1>
            {!isShareView && (
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 border border-theme-border text-theme-text-muted hover:text-theme-text hover:border-theme-text transition-colors flex-shrink-0"
                title="Share this property"
              >
                <ShareIcon className="h-5 w-5" />
                <span className={themeClasses.text.button}>Share</span>
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {formatLocation() && (
              <div className="flex items-center space-x-2">
                <MapPinIcon className="h-5 w-5 text-theme-text-muted" />
                <p className={themeClasses.text.body}>{formatLocation()}</p>
              </div>
            )}
            {/* Villa-specific: Bedrooms & Bathrooms */}
            {hotel.bedrooms && (
              <p className={themeClasses.text.body}>
                {hotel.bedrooms} {hotel.bedrooms === 1 ? 'Bedroom' : 'Bedrooms'}
                {hotel.bathrooms && ` · ${hotel.bathrooms} ${hotel.bathrooms === 1 ? 'Bathroom' : 'Bathrooms'}`}
              </p>
            )}
            {/* Starting Rate for villas */}
            {hotel.startingRate && (
              <p className={themeClasses.text.body}>
                From ${hotel.startingRate.toLocaleString()}/night
              </p>
            )}
          </div>
        </div>

        {/* Image Slideshow */}
        <div className="mb-12">
          <ImageSlideshow images={images} alt={hotel.name} />
        </div>

        {/* Hotel Info */}
        <div className="pb-16">

          {/* Content and Map Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - All Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Description */}
              {description && (
                <div className="space-y-4">
                  <h2 className={themeClasses.title.h4}>About</h2>
                  <div
                    className={`${themeClasses.text.body} [&_p]:mb-2 [&_p:last-child]:mb-0 [&_br]:hidden`}
                    dangerouslySetInnerHTML={{ __html: stripInlineStyles(description) }}
                  />
                </div>
              )}

              {/* Live Rates - Commented out for now
              <div className="space-y-4">
                <h2 className={themeClasses.title.h4}>Check Availability</h2>
                <HotelRates vendorId={hotel.id} />
              </div>
              */}

              {/* Benefits */}
              {benefits.length > 0 && (
                <div className="space-y-4">
                  <h2 className={`${themeClasses.title.h4} normal-case`}>FHxD BENEFITS</h2>
                  <ul className="space-y-1">
                    {benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-theme-accent mt-1">•</span>
                        <span className={themeClasses.text.body}>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Villa-specific: Features */}
              {hotel.features && (
                <div className="space-y-4">
                  <h2 className={themeClasses.title.h4}>Features</h2>
                  <div
                    className={`${themeClasses.text.body} [&_p]:mb-2 [&_p:last-child]:mb-0`}
                    dangerouslySetInnerHTML={{ __html: hotel.features }}
                  />
                </div>
              )}

              {/* Villa-specific: Staff & Services */}
              {hotel.staffAndServices && (
                <div className="space-y-4">
                  <h2 className={themeClasses.title.h4}>Staff & Services</h2>
                  <div
                    className={`${themeClasses.text.body} [&_p]:mb-2 [&_p:last-child]:mb-0`}
                    dangerouslySetInnerHTML={{ __html: hotel.staffAndServices }}
                  />
                </div>
              )}

              {/* Villa-specific: Details */}
              {hotel.details && (
                <div className="space-y-4">
                  <h2 className={themeClasses.title.h4}>Details</h2>
                  <div
                    className={`${themeClasses.text.body} [&_p]:mb-2 [&_p:last-child]:mb-0`}
                    dangerouslySetInnerHTML={{ __html: hotel.details }}
                  />
                </div>
              )}

              {/* Villa-specific: Special Notes */}
              {hotel.specialNotes && (
                <div className="space-y-4">
                  <h2 className={themeClasses.title.h4}>Special Notes</h2>
                  <div
                    className={`${themeClasses.text.body} [&_p]:mb-2 [&_p:last-child]:mb-0`}
                    dangerouslySetInnerHTML={{ __html: hotel.specialNotes }}
                  />
                </div>
              )}
            </div>

            {/* Right Column - Map */}
            {hotel.latitude && hotel.longitude && (
              <div className="min-w-0 overflow-hidden lg:sticky lg:top-28 lg:self-start">
                <MapProvider>
                  <LocationMap
                    latitude={hotel.latitude}
                    longitude={hotel.longitude}
                    name={hotel.name}
                    className="h-[200px] lg:h-[250px]"
                  />
                </MapProvider>
              </div>
            )}
          </div>

          {/* CTA - hidden in share view or when authenticated */}
          {!isShareView && !isAuthenticated && (
            <div className="border-t border-theme-border pt-12 mt-12">
              <div className="text-center space-y-6">
                <h2 className={themeClasses.title.h3}>READY TO GET STARTED?</h2>
                <p className={themeClasses.text.body}>
                  Discover the FHxD world-class travel partner collection and earn more, faster.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button variant="primary" onClick={() => navigate('/signup')}>
                    Advisor Sign Up
                  </Button>
                  <Button variant="secondary" onClick={() => navigate('/login')}>
                    Sign In
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Request Booking - bottom center, authenticated advisors only */}
          {isAuthenticated && !isShareView && (
            <div className="border-t border-theme-border pt-12 mt-12 flex justify-center">
              <SubmitRequestButton
                variant="primary"
                size="lg"
                vendorId={hotel.id}
                initialPreferredHotel={hotel.name}
              >
                Request Booking
              </SubmitRequestButton>
            </div>
          )}
        </div>
      </div>

      {/* Unlock modal — shown on load for signed-out visitors (not share view) */}
      {showSignUpPopup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          {/* Backdrop with blur */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
          {/* Modal */}
          <div className="relative bg-theme-bg border border-theme-border shadow-xl max-w-md w-full p-8 animate-in fade-in zoom-in-95 duration-200">
            <div className="text-center space-y-6">
              <h3 className={themeClasses.title.h3}>
                Unlock Exclusive Rates
              </h3>
              <p className={themeClasses.text.body}>
                Join FHxD to access exclusive rates and enhanced commissions at {hotel.name}.
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  variant="primary"
                  onClick={() => navigate('/signup')}
                  className="w-full"
                >
                  Advisor Sign Up
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => navigate('/login')}
                  className="w-full"
                >
                  Already a Member? Sign In
                </Button>
              </div>
              <button
                type="button"
                onClick={navigateBackToCollection}
                className={`${themeClasses.text.muted} hover:text-theme-text transition-colors text-sm`}
              >
                ← Back to Collection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HotelDetail;
